// MCHD Room Scheduler - Building Management Hook
// React Query hooks for building management operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../useAuth';
import {
  getBuildings,
  getBuildingById,
  createBuilding,
  updateBuilding,
  deleteBuilding,
  getBuildingStats,
  getBuildingsForSelection,
  type GetBuildingsParams,
  type Building,
  type CreateBuildingData,
  type UpdateBuildingData,
} from '../../api/endpoints/buildings.js';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const buildingQueryKeys = {
  all: ['buildings'] as const,
  lists: () => [...buildingQueryKeys.all, 'list'] as const,
  list: (params: GetBuildingsParams) => [...buildingQueryKeys.lists(), params] as const,
  details: () => [...buildingQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...buildingQueryKeys.details(), id] as const,
  stats: (id: string) => [...buildingQueryKeys.details(), id, 'stats'] as const,
  selection: () => [...buildingQueryKeys.all, 'selection'] as const,
};

// =============================================================================
// BUILDING LIST HOOK
// =============================================================================

/**
 * Hook to get buildings list with pagination and filtering
 * @param params - Query parameters
 * @returns Building list query result
 */
export function useBuildings(params: GetBuildingsParams = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: buildingQueryKeys.list(params),
    queryFn: async () => {
      const response = await getBuildings(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch buildings');
      }
      return response.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get buildings for selection/dropdown
 * @returns Buildings selection query result
 */
export function useBuildingsForSelection() {
  return useQuery({
    queryKey: buildingQueryKeys.selection(),
    queryFn: () => getBuildingsForSelection(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =============================================================================
// BUILDING DETAIL HOOKS
// =============================================================================

/**
 * Hook to get building by ID
 * @param id - Building ID
 * @returns Building detail query result
 */
export function useBuildingById(id: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: buildingQueryKeys.detail(id),
    queryFn: () => getBuildingById(id),
    enabled: !!token && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors or not found
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get building statistics
 * @param id - Building ID
 * @returns Building statistics query result
 */
export function useBuildingStats(id: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: buildingQueryKeys.stats(id),
    queryFn: () => getBuildingStats(id),
    enabled: !!token && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors or not found
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =============================================================================
// BUILDING MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new building
 * @returns Building creation mutation
 */
export function useCreateBuilding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBuilding,
    onSuccess: (_data) => {
      // Invalidate and refetch building lists
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.lists(),
      });
      
      // Invalidate buildings for selection
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.selection(),
      });

      // Add the new building to the cache
      if (_data.success && _data.data?.building) {
        queryClient.setQueryData(
          buildingQueryKeys.detail(_data.data.building.id),
          _data
        );
      }
    },
    onError: (error: any) => {
      console.error('Error creating building:', error);
    },
  });
}

/**
 * Hook to update a building
 * @returns Building update mutation
 */
export function useUpdateBuilding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBuildingData }) =>
      updateBuilding(id, data),
    onSuccess: (_data, variables) => {
      // Invalidate and refetch building lists
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.lists(),
      });
      
      // Invalidate buildings for selection
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.selection(),
      });

      // Update the specific building in cache
      queryClient.setQueryData(
        buildingQueryKeys.detail(variables.id),
        _data
      );

      // Invalidate building stats
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.stats(variables.id),
      });
    },
    onError: (error: any) => {
      console.error('Error updating building:', error);
    },
  });
}

/**
 * Hook to delete a building
 * @returns Building deletion mutation
 */
export function useDeleteBuilding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBuilding,
    onSuccess: (_data, buildingId) => {
      // Remove the building from all caches
      queryClient.removeQueries({
        queryKey: buildingQueryKeys.detail(buildingId),
      });
      
      queryClient.removeQueries({
        queryKey: buildingQueryKeys.stats(buildingId),
      });

      // Invalidate and refetch building lists
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.lists(),
      });
      
      // Invalidate buildings for selection
      queryClient.invalidateQueries({
        queryKey: buildingQueryKeys.selection(),
      });
    },
    onError: (error: any) => {
      console.error('Error deleting building:', error);
    },
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to prefetch building details
 * @param id - Building ID to prefetch
 */
export function usePrefetchBuilding(id: string) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.prefetchQuery({
      queryKey: buildingQueryKeys.detail(id),
      queryFn: () => getBuildingById(id),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };
}

/**
 * Hook to prefetch building statistics
 * @param id - Building ID to prefetch stats for
 */
export function usePrefetchBuildingStats(id: string) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.prefetchQuery({
      queryKey: buildingQueryKeys.stats(id),
      queryFn: () => getBuildingStats(id),
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  };
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type {
  Building,
  CreateBuildingData,
  UpdateBuildingData,
  GetBuildingsParams,
};
